import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { BiArrowBack } from 'react-icons/bi';
import { platformAPI } from '../../services/platformAPI';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import './tender/CustomerMaster.scss';
import './tender/PersonnelPage.scss';
import './tender/TimesheetsPage.scss';

const STATUS_LABELS = {
  PENDING: 'Pending',
  IN_PROGRESS: 'In progress',
  OVERDUE: 'Overdue',
  COMPLETED: 'Approved',
  CANCELLED: 'Returned'
};

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const Field = ({ label, wide, children }) => (
  <Form.Group className={`cust-field${wide ? ` cust-field--${wide}` : ''}`}>
    <Form.Label>{label}</Form.Label>
    {children}
  </Form.Group>
);

const ApprovalRecordPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { workItemId } = useParams();
  const isDecide = location.pathname.endsWith('/decide');
  const [item, setItem] = useState(null);
  const [subject, setSubject] = useState(null);
  const [comments, setComments] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState('');
  const [error, setError] = useState('');

  useDocumentTitle(item?.title || 'Approval');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const response = await platformAPI.getApproval(workItemId);
        if (!cancelled) {
          setItem(response.data?.data?.item || null);
          setSubject(response.data?.data?.subject || null);
        }
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.message || 'Unable to open this request');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workItemId]);

  const act = async (action) => {
    if (!item) return;
    if (action === 'return' && !comments.trim()) {
      setError('A return needs a note so the person who raised it knows what to change.');
      return;
    }
    setSaving(action);
    setError('');
    try {
      await platformAPI.approvalAction(item._id, {
        action,
        comments,
        expectedVersion: item.objectVersion
      });
      navigate('/apps/tender/approvals');
    } catch (err) {
      setError(err.response?.data?.message || 'The decision could not be saved');
      setSaving('');
    }
  };

  if (loading) return <Spinner animation="border" size="sm" />;
  if (!item) {
    return (
      <div className="cust-page">
        <Alert variant="danger">{error || 'This request is no longer available.'}</Alert>
      </div>
    );
  }

  const tenderLabel = item.tenderReference
    ? `${item.tenderReference}${item.tenderTitle ? ` — ${item.tenderTitle}` : ''}`
    : '—';
  const statusLabel = STATUS_LABELS[item.status] || item.status;
  const lines = subject?.lines || [];
  const showDecision = isDecide && item.canDecide;

  return (
    <div className="cust-page">
      <div className="cust-record pers-record cust-record--view">
        <header className="cust-mast">
          <div className="cust-mast__identity">
            <button type="button" className="cust-page__back" onClick={() => navigate('/apps/tender/approvals')}>
              <BiArrowBack />
              Approvals
            </button>
            <div className="cust-mast__row">
              <span className="cust-mark" aria-hidden>{(item.typeLabel || 'A').slice(0, 1)}</span>
              <div className="cust-mast__copy">
                <p className="cust-mast__eyebrow">{isDecide ? 'Deciding request' : 'Approval request'}</p>
                <h1>{item.title}</h1>
                <div className="cust-mast__meta">
                  <span className="cust-chip">{item.typeLabel || item.type}</span>
                  <span className={`ts-page__badge ts-page__badge--${item.status === 'COMPLETED' ? 'approved' : item.status === 'CANCELLED' ? 'returned' : 'submitted'}`}>
                    {statusLabel}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="cust-mast__actions">
            {!isDecide && item.canDecide ? (
              <Button size="sm" variant="primary" onClick={() => navigate(`/apps/tender/approvals/${item._id}/decide`)}>
                Decide
              </Button>
            ) : (
              <Button size="sm" variant="outline-primary" onClick={() => navigate('/apps/tender/approvals')}>
                Back to list
              </Button>
            )}
          </div>
        </header>

        {error ? <Alert variant="danger" className="cust-record__alert">{error}</Alert> : null}

        <div className="cust-record__body">
          <div className="cust-record__main">
            <section className="cust-section">
              <header className="cust-section__head">
                <span>01</span>
                <div>
                  <h2>The request</h2>
                  <p>Where this decision came from, and when it is due.</p>
                </div>
              </header>
              <div className="cust-fields">
                <Field label="Type" wide="4">
                  <Form.Control value={item.typeLabel || item.type || ''} disabled />
                </Field>
                <Field label="Tender" wide="8">
                  <Form.Control value={tenderLabel} disabled />
                </Field>
                <Field label="Raised by" wide="4">
                  <Form.Control value={item.raisedByName || '—'} disabled />
                </Field>
                <Field label="Waiting since" wide="4">
                  <Form.Control value={formatDate(item.createdAt)} disabled />
                </Field>
                <Field label="Due" wide="4">
                  <Form.Control value={formatDate(item.dueDate)} disabled />
                </Field>
                <Field label="Note" wide="12">
                  <Form.Control as="textarea" rows={2} value={item.description || ''} disabled />
                </Field>
              </div>
            </section>

            <section className="cust-section">
              <header className="cust-section__head">
                <span>02</span>
                <div>
                  <h2>What you are deciding</h2>
                  <p>The record this request will update.</p>
                </div>
              </header>
              <div className="cust-fields">
                {lines.map(([label, value]) => (
                  <Field key={label} label={label} wide={String(value || '').length > 80 ? '12' : '6'}>
                    <Form.Control
                      as={String(value || '').length > 80 ? 'textarea' : 'input'}
                      rows={4}
                      value={value || '—'}
                      disabled
                    />
                  </Field>
                ))}
              </div>
              {subject?.recordPath ? (
                <p className="cust-section__note">
                  <Link to={subject.recordPath}>{subject.recordLabel || 'Open the record'}</Link>
                </p>
              ) : null}
            </section>

            <section className="cust-section">
              <header className="cust-section__head">
                <span>03</span>
                <div>
                  <h2>Decision</h2>
                  <p>Approve accepts the record. Return sends it back with your note.</p>
                </div>
              </header>
              {showDecision ? (
                <div className="cust-fields">
                  <Field label="Comments" wide="12">
                    <Form.Control
                      as="textarea"
                      rows={4}
                      value={comments}
                      onChange={(event) => setComments(event.target.value)}
                      placeholder="Required when you return the request"
                    />
                  </Field>
                  <div className="cust-field cust-field--12">
                    <Button size="sm" variant="primary" disabled={Boolean(saving)} onClick={() => act('approve')}>
                      {saving === 'approve' ? 'Saving…' : 'Approve'}
                    </Button>
                    {' '}
                    <Button size="sm" variant="outline-primary" disabled={Boolean(saving)} onClick={() => act('return')}>
                      {saving === 'return' ? 'Saving…' : 'Return'}
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="cust-section__note">
                  {subject?.self
                    ? 'You prepared this request. Someone else has to decide it.'
                    : item.canDecide
                      ? 'Open Decide to approve this request or send it back.'
                      : 'This request is not waiting on you.'}
                </p>
              )}
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApprovalRecordPage;
